import { getProductTimeline, STAGES, TIME_ONE_SECOND_FIX } from '@/constants/powerStream';
import EfScrollAni from '@/efscrollani';
import { getHostApi } from '@/utils';
import getDefaultHeader from '@/utils/singUtil/getDefaultHeader';
import { useGlobal } from '@/utils/useGlobal';
import dynamic from 'next/dynamic';

// 两种场景可以使用lazy 或 dynamic 1.非首屏渲染的模块（包含条件渲染）2.拆包大小并且做seo优化可以使用 ssr: true
const Teaser: any = dynamic(() => import('./teaser'), { ssr: true });
const Detail: any = dynamic(() => import('./detail'), { ssr: true });

export default function PowerPC({ ecoflowflag, ecoflowflag2, ecoflowflag3 }: any) {
    const { useRef, getSystemTime, useState, useRouter, useEffect } = useGlobal();
    const router = useRouter();
    const { locale } = router;

    const TIMELINE = getProductTimeline(locale, true);
    // 服务器时间
    const serverTimeStamp: any = useRef(getSystemTime());
    // 倒计时三个时间段
    const startTimeStamp: number = new Date(TIMELINE?.teaser || 0).getTime();
    const endTimeStamp: number = new Date(TIMELINE?.detail || 0).getTime();
    // 计时器
    const timer: any = useRef(null);
    const clientTimeStamp = useRef(new Date().getTime());

    const [currentStage, setCurrentStage] = useState(() => {
        // 测试环境serverTimeStamp获取会异常， 异常时纠正
        if (serverTimeStamp.current < 1000000) {
            serverTimeStamp.current = clientTimeStamp.current;
        }
        // 详情阶段
        if (ecoflowflag || (serverTimeStamp.current >= endTimeStamp && endTimeStamp !== 0)) {
            return STAGES.DETAIL;
        }
        // 预热阶段
        if (ecoflowflag2 || (serverTimeStamp.current >= startTimeStamp && startTimeStamp !== 0)) {
            return STAGES.TEASER;
        }
        return STAGES.UNKNOWN;
    });

    const visibilityChange = useRef(true);

    if (typeof window !== 'undefined' && !window.AniContainer) {
        window.AniContainer = new EfScrollAni();
    }

    const onVisibleChange = async () => {
        if (window.document.visibilityState === 'visible') {
            visibilityChange.current = true;
            await getServiceTime();
        } else {
            visibilityChange.current = false;
        }
    };

    const getServiceTime = async () => {
        const API_HOST = getHostApi(locale).NEXT_PUBLIC_API_HOST;
        const headers = getDefaultHeader(locale);

        try {
            const spuData = await fetch(`${API_HOST}/serverTimestamp?locale=${locale}`, {
                headers,
            });
            const globalData = await spuData.json();
            serverTimeStamp.current = globalData?.data?.timestamp || new Date().getTime();
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        // 处理倒计时
        const handleCountDown = async () => {
            const nowTime = new Date().getTime();
            const disTime = nowTime - clientTimeStamp.current;

            // 如果超过这个时间 && tab处于当前页面，需从服务器拉取最新的时间去修正
            if (disTime > TIME_ONE_SECOND_FIX && visibilityChange.current) {
                await getServiceTime();
            } else {
                serverTimeStamp.current = serverTimeStamp.current + disTime;
            }

            // 详情阶段
            if (ecoflowflag || (serverTimeStamp.current >= endTimeStamp && endTimeStamp !== 0)) {
                // 当前阶段本就是详情时，不需要set，否则会引起不必要的重绘重排，定时器任务每秒一次，严重影响性能
                currentStage !== STAGES.DETAIL && setCurrentStage(STAGES.DETAIL);
                return;
            }

            // 预热阶段
            if (ecoflowflag2 || (serverTimeStamp.current >= startTimeStamp && startTimeStamp !== 0)) {
                // 此处不能执行清除定时器任务， 到进入详情阶段之前都必须一直执行定时器，否则如果预热页一直打开的情况下，时间点到达众筹阶段或者详情阶段不会自动切换
                currentStage !== STAGES.TEASER && setCurrentStage(STAGES.TEASER);
            }

            // 预热开始前阶段: 有预热、无预热有众筹、无预热无众筹
            if (!ecoflowflag && !ecoflowflag2) {
                if (
                    (serverTimeStamp.current < startTimeStamp && startTimeStamp !== 0) ||
                    (serverTimeStamp.current < endTimeStamp && startTimeStamp === 0 && endTimeStamp !== 0)
                ) {
                    return router.push('/404');
                }
            }

            clientTimeStamp.current = nowTime;
            timer.current = setTimeout(handleCountDown, 1000);
        };

        timer.current = setTimeout(handleCountDown, 1000);
        window.document.addEventListener('visibilitychange', onVisibleChange);

        return () => {
            clearTimeout(timer.current);
            AniContainer?.destroy();
        };
    }, []);

    return (
        <>
            {currentStage === STAGES.TEASER && <Teaser />}
            {currentStage === STAGES.DETAIL && <Detail />}
        </>
    );
}
